import { useMemo } from 'react'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { ActivityLogPromiseClient } from '@trustero/trustero-api-web/lib/activity/activity_grpc_web_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import {
  GetActivityLogRequest,
  GetActivityLogCitationRequest,
  ActivityLogEntries,
  ACTIVITY_CITATION_TYPE,
} from '@trustero/trustero-api-web/lib/activity/activity_pb'
import { useInAudit } from 'src/context/AuditContext'
import {
  GrpcInfiniteResponse,
  NewGrpcInfiniteResponse,
} from '../../components/async/hooks/types'
import { useSwrInfiniteGrpc } from '../../components/async/useSwrInfiniteGrpc'

export const DEFAULT_LOOKBACK_DAYS = 90
const DAY_IN_MILLIS = 24 * 60 * 60 * 1000

const MODEL_TO_CITATION_TYPE: Partial<
  Record<MODEL_TYPE, ACTIVITY_CITATION_TYPE>
> = {
  [MODEL_TYPE.AUDIT]: ACTIVITY_CITATION_TYPE.AUDIT,
  [MODEL_TYPE.COMPLIANCE_FRAMEWORK]: ACTIVITY_CITATION_TYPE.FRAMEWORK,
  [MODEL_TYPE.POLICY]: ACTIVITY_CITATION_TYPE.POLICY,
  [MODEL_TYPE.CONTROL]: ACTIVITY_CITATION_TYPE.CONTROL,
  [MODEL_TYPE.EVIDENCE]: ACTIVITY_CITATION_TYPE.EVIDENCE,
  [MODEL_TYPE.REQUEST]: ACTIVITY_CITATION_TYPE.REQUEST,
  [MODEL_TYPE.RISK]: ACTIVITY_CITATION_TYPE.RISK,
  [MODEL_TYPE.VENDOR]: ACTIVITY_CITATION_TYPE.VENDOR,
  [MODEL_TYPE.RECEPTOROBJ]: ACTIVITY_CITATION_TYPE.RECEPTOR_OBJ,
  [MODEL_TYPE.SCHEDULE]: ACTIVITY_CITATION_TYPE.SCHEDULE,
  [MODEL_TYPE.QUESTIONNAIRE]: ACTIVITY_CITATION_TYPE.QUESTIONNAIRE,
  [MODEL_TYPE.USEROBJ]: ACTIVITY_CITATION_TYPE.USER_OBJ,
}

export const useChangeLogEntries = (
  itemsPerPage: number,
  pageNumber: number,
  subjectModelIds?: string[],
  subjectIds?: string[],
  subjectType?: MODEL_TYPE,
): GrpcInfiniteResponse<ActivityLogEntries> => {
  const { auditId } = useInAudit()
  const targetId =
    subjectModelIds?.find((v) => !!v) || subjectIds?.find((v) => !!v)
  const citationType = subjectType
    ? MODEL_TO_CITATION_TYPE[subjectType]
    : undefined

  const serviceMethod = targetId
    ? ActivityLogPromiseClient.prototype.getActivityLogsCitation
    : ActivityLogPromiseClient.prototype.getActivityLogs

  // Truncate to a day boundary and memoize so this stays referentially stable across
  // renders — useSwrInfiniteGrpc calls requestBuilder() to derive its SWR cache key, and
  // a value that ticks on every call (e.g. raw Date.now()) makes the key never settle,
  // causing an infinite refetch loop.
  const beginTime = useMemo(() => {
    const today = new Date()
    const todayUtcMillis = Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
    )
    return Timestamp.fromDate(
      new Date(todayUtcMillis - DEFAULT_LOOKBACK_DAYS * DAY_IN_MILLIS),
    )
  }, [])

  const requestBuilder = () => {
    if (targetId) {
      const request = new GetActivityLogCitationRequest()
        .setEntriesPerPage(itemsPerPage)
        .setPageNumber(pageNumber)
        .setCitationId(targetId)
        .setBeginTime(beginTime)
      if (citationType !== undefined) {
        request.setCitationType(citationType)
      }
      if (auditId) {
        request.setAuditId(auditId)
      }
      return request
    }

    const request = new GetActivityLogRequest()
      .setEntriesPerPage(itemsPerPage)
      .setPageNumber(pageNumber)
      .setBeginTime(beginTime)
    if (auditId) {
      request.setAuditId(auditId)
    }
    return request
  }

  const { response } = useSwrInfiniteGrpc(
    serviceMethod as unknown as Parameters<typeof useSwrInfiniteGrpc>[0],
    requestBuilder,
    pageNumber,
  )
  return NewGrpcInfiniteResponse(response)
}
